import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGeoMarkupStore } from '../store';
import { selectCurrentWorkspaceId } from '../store/selectors/authSelectors';
import type { WorkspaceMember } from '../types/workspace';

export interface UseWorkspaceMemberSuggestionsOptions {
  enabled?: boolean;
}

/**
 * Thin reader over the shared `workspaceMembers` slice. The member list is
 * fetched once per workspace and cached in the store, so multiple consumers
 * (filter control + filter pills + assignee pickers) no longer each fire their
 * own `GET /workspaces/{id}/members` request.
 *
 * Public API is intentionally unchanged: returns `{ members, isLoading }` and
 * accepts an `{ enabled }` option. When `enabled` is false (or there is no
 * active workspace), it returns an empty list and never triggers a fetch.
 */
export function useWorkspaceMemberSuggestions(
  options: UseWorkspaceMemberSuggestionsOptions = {}
): {
  members: WorkspaceMember[];
  isLoading: boolean;
} {
  const { enabled = true } = options;
  const workspaceId = useGeoMarkupStore(selectCurrentWorkspaceId);

  // Array selector → useShallow to avoid re-renders on reference-only changes.
  const sliceMembers = useGeoMarkupStore(
    useShallow((state) => state.workspaceMembers.members)
  );
  const sliceIsLoading = useGeoMarkupStore(
    (state) => state.workspaceMembers.isLoading
  );
  const fetchMemberSuggestions = useGeoMarkupStore(
    (state) => state.workspaceMembers.fetchMemberSuggestions
  );

  useEffect(() => {
    if (enabled && workspaceId) {
      void fetchMemberSuggestions(workspaceId);
    }
    // Intentionally do NOT clear the shared cache when disabled — other
    // consumers may still need it. The return below scopes empty results to
    // this caller only.
  }, [workspaceId, enabled, fetchMemberSuggestions]);

  // Preserve the legacy `enabled` semantics: a disabled caller (or one without
  // a workspace) sees an empty list and a settled loading state.
  const isActive = enabled && Boolean(workspaceId);
  return {
    members: isActive ? sliceMembers : [],
    isLoading: isActive ? sliceIsLoading : false,
  };
}
